import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift, Plus, Search, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { SpinnerBlock } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  getTikTokOutreachQuota,
  listTikTokCollaborations,
  type TikTokCollaboration,
  type TikTokCollaborationStatus,
  type TikTokCreatorPick,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { TikTokBlockedState } from "../TikTokBlockedState";
import { blockFromError, errorMessage, shortDate } from "../tiktokFormat";
import { InviteDetailSheet } from "./InviteDetailSheet";
import { NewInviteSheet } from "./NewInviteSheet";

const FILTERS: Array<{ value: TikTokCollaborationStatus; label: string }> = [
  { value: "VALID", label: "Active" },
  { value: "EXPIRING", label: "Ending soon" },
  { value: "COMPLETED", label: "Ended" },
];

function daysLeft(iso: string | null): string {
  if (!iso) return "";
  const days = Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
  if (days < 0) return `Ended ${shortDate(iso)}`;
  if (days === 0) return "Ends today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

function Funnel({ c }: { c: TikTokCollaboration }) {
  const steps = [
    { label: "invited", value: c.invitedCount },
    { label: "added", value: c.showcaseCount },
    { label: "posted", value: c.contentCount },
  ];
  return (
    <span className="flex items-center gap-3 text-xs tabular-nums text-muted-foreground">
      {steps.map((s) => (
        <span key={s.label}>
          <span className="font-semibold text-foreground">{s.value}</span> {s.label}
        </span>
      ))}
    </span>
  );
}

/**
 * Collab invites (TikTok target collaborations). TikTok is the record: the
 * list reads live from it, with the weekly outreach allowance on top.
 */
export function InvitesTab({
  draftCreators,
  onDraftConsumed,
  onMessage,
}: {
  /** Creators handed over from search, samples or messages; opens the new-invite sheet. */
  draftCreators: TikTokCreatorPick[] | null;
  onDraftConsumed: () => void;
  onMessage: (creator: TikTokCreatorPick) => void;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TikTokCollaborationStatus>("VALID");
  const [keyword, setKeyword] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [initialCreators, setInitialCreators] = useState<TikTokCreatorPick[]>([]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(keyword.trim()), 350);
    return () => clearTimeout(t);
  }, [keyword]);

  useEffect(() => {
    if (draftCreators) {
      setInitialCreators(draftCreators);
      setNewOpen(true);
      onDraftConsumed();
    }
  }, [draftCreators, onDraftConsumed]);

  const list = useQuery({
    queryKey: ["tiktok-collabs", status, query],
    queryFn: () => listTikTokCollaborations(status, query),
    retry: false,
  });
  const quota = useQuery({ queryKey: ["tiktok-outreach-quota"], queryFn: getTikTokOutreachQuota, retry: false });

  const block = list.error ? blockFromError(list.error) : null;
  if (block && block.kind !== "error") {
    return <TikTokBlockedState block={block} feature="collab invites" onRetry={() => list.refetch()} />;
  }

  const startNew = () => {
    setInitialCreators([]);
    setNewOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          {quota.data && !quota.data.unlimited ? (
            <p className="text-sm text-muted-foreground">
              <span className="font-medium tabular-nums text-foreground">{quota.data.remaining ?? "?"}</span> of {quota.data.total ?? "?"} creator
              outreach left this week · resets {shortDate(quota.data.periodEnd)}
              {quota.data.maxFollowerCount !== null ? ` · creators up to ${formatCompactCount(quota.data.maxFollowerCount)} followers` : ""}
            </p>
          ) : quota.data?.unlimited ? (
            <p className="text-sm text-muted-foreground">Unlimited creator outreach this week</p>
          ) : null}
        </div>
        <Button type="button" onClick={startNew} className="shrink-0">
          <Plus className="h-4 w-4" />
          New invite
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Invite status" className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="tab"
              aria-selected={status === f.value}
              onClick={() => setStatus(f.value)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                status === f.value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search invites" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {list.isLoading ? (
          <SpinnerBlock className="py-16" />
        ) : list.error ? (
          <EmptyState bordered={false} title="Invites could not load" body={errorMessage(list.error)} action={<Button variant="outline" onClick={() => list.refetch()}>Try again</Button>} />
        ) : (list.data?.items ?? []).length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<Send />}
            title={query ? "No invites match" : status === "VALID" ? "No active invites" : "Nothing here yet"}
            body="Offer creators a higher commission and free samples on the products you pick"
            action={
              status === "VALID" && !query ? (
                <Button onClick={startNew}>
                  <Plus className="h-4 w-4" />
                  New invite
                </Button>
              ) : undefined
            }
          />
        ) : (
          list.data!.items.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setOpenId(c.id)}
              className="flex w-full flex-col gap-2 border-b border-border px-4 py-3 text-left last:border-b-0 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
            >
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-foreground">{c.name}</span>
                  {c.freeSample && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                      <Gift className="h-3 w-3" />
                      {c.sampleApprovalExempt ? "Samples auto-approved" : "Free samples"}
                    </span>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {c.productCount} product{c.productCount === 1 ? "" : "s"} · {daysLeft(c.endTime)}
                </span>
              </span>
              <Funnel c={c} />
            </button>
          ))
        )}
      </div>

      <InviteDetailSheet id={openId} onOpenChange={(open) => !open && setOpenId(null)} onMessage={onMessage} />
      <NewInviteSheet
        open={newOpen}
        onOpenChange={setNewOpen}
        initialCreators={initialCreators}
        onCreated={(id) => {
          queryClient.invalidateQueries({ queryKey: ["tiktok-collabs"] });
          queryClient.invalidateQueries({ queryKey: ["tiktok-outreach-quota"] });
          if (id) setOpenId(id);
        }}
      />
    </div>
  );
}
